import { FC, useMemo, useRef, useState } from 'react'

import { clsx } from 'clsx'

import { useCalendarFocus } from 'shared/hooks'
import { differenceDate } from 'shared/lib/dateFormat'
import { ButtonCalendar } from 'shared/ui/button-calendar'
import { CalendarHeader } from 'shared/ui/calendar-header'
import { DateInfo, MonthWrapper } from 'shared/ui/month-wrapper'

import { prepareDate } from '../lib'

import { IRange, Props, TYPE_DATE } from './model'
import styles from './styles.module.scss'

function formatDateFetch(date?: string): string | undefined {
  if (!date) return undefined

  const [year, month, day] = date.split('/')

  const formattedMonth = month.padStart(2, '0')
  const formattedDay = day.padStart(2, '0')

  return `${year}-${formattedMonth}-${formattedDay}`
}

export const DateRangePicker: FC<Props> = ({
  header,
  difficult,
  direction,
  focusDate,
  showRange,
  showResetIcon,
  dateFormat,
  activeDays,
  selectedDate,
  selectedDates,
  indexDirection,
  calendarPrices,
  typeCalendar = 'avia',
  confirmText,
  placeholders,
  onChange,
  triggerCalendar,
  maxMonth,
  maxRangeDays,
}) => {
  const headerRef = useRef<HTMLDivElement>(null)
  const requiresRange = typeCalendar === 'insurance'

  const [selected, setSelected] = useState<IRange | null>({
    startDate: selectedDate.startDate && prepareDate(selectedDate.startDate),
    endDate: selectedDate.endDate && prepareDate(selectedDate.endDate),
  })

  const onClick = (date: string) => {
    const startDateTimestamp = selected?.startDate
      ? new Date(selected.startDate).getTime()
      : null
    const currentDateTimestamp = new Date(date).getTime()
    const exceedsMaxRange =
      selected?.startDate &&
      !selected.endDate &&
      maxRangeDays &&
      Math.abs(
        differenceDate({
          dateStart: selected.startDate,
          dateEnd: date,
          type: 'day',
        }),
      ) >= maxRangeDays

    if (exceedsMaxRange) return

    if (activeDays?.length || difficult) {
      setSelected({ startDate: date })
    } else if (
      selected?.startDate &&
      !selected?.endDate &&
      startDateTimestamp
    ) {
      setSelected((prev) =>
        currentDateTimestamp < startDateTimestamp
          ? { startDate: date, endDate: prev?.startDate }
          : { startDate: prev?.startDate, endDate: date },
      )
    } else {
      if (
        !difficult &&
        triggerCalendar &&
        direction?.from?.cityIataCode &&
        direction.to?.cityIataCode
      ) {
        triggerCalendar(formatDateFetch(date))
      }
      setSelected({ startDate: date })
    }
  }

  const onSelect = () => {
    if (requiresRange && (!selected?.startDate || !selected.endDate)) {
      return
    }

    if (onChange && selected) {
      onChange({
        startDate: formatDateFetch(selected.startDate),
        endDate: formatDateFetch(selected.endDate),
      })
    }
  }

  useCalendarFocus(focusDate, { headerRef })

  const onResetValue = (date: TYPE_DATE) => {
    if (date === TYPE_DATE.START_DATE) {
      setSelected(null)
      if (
        !difficult &&
        triggerCalendar &&
        direction?.from?.cityIataCode &&
        direction.to?.cityIataCode
      ) {
        triggerCalendar()
      }
    } else {
      setSelected((data) => ({ ...data, endDate: undefined }))
      triggerCalendar &&
        triggerCalendar(formatDateFetch(selected?.startDate) || '')
    }
  }

  const dateListPrice: DateInfo[] | undefined = useMemo(
    () =>
      calendarPrices?.map((day) => ({
        date: prepareDate(String(day.back_date || day.dep_date)),
        content: String(day.price),
        isCheapest: !!day?.is_cheapest,
        color: day?.color,
      })),
    [calendarPrices],
  )

  const selectedDatesList = difficult ? selectedDates : undefined

  return (
    <div className={clsx('container', styles.container)}>
      <div
        ref={headerRef}
        className={styles.header}
      >
        {header}
        <div className={styles.range}>
          {showRange && (
            <CalendarHeader
              showResetIcon={showResetIcon}
              dateFormat={dateFormat}
              selected={selected}
              placeholders={placeholders}
              onResetValue={onResetValue}
            />
          )}
        </div>
      </div>

      <MonthWrapper
        indexDirection={indexDirection}
        activeDays={activeDays}
        isDifficult={difficult}
        dateInfo={dateListPrice}
        selected={selected || null}
        selectedDays={selectedDatesList}
        onClick={onClick}
        maxMonth={maxMonth}
        maxRangeDays={maxRangeDays}
      />

      <ButtonCalendar
        onClick={onSelect}
        selectedDates={selected}
        typeCalendar={typeCalendar}
        confirmText={confirmText}
      />
    </div>
  )
}
